<script lang="ts">
  // AdSense 광고 슬롯 — 인피드(목록)와 디스플레이(글 상세) 두 variant 를 겸한다.
  //
  // 개발·프로덕션 판정은 서버가 이미 내린다: production 이 아니면
  // `<meta name="google-adsense-account">` 가 레이아웃에 없으므로, 그 meta 가
  // 없거나 slot 이 비어 있으면(단위 미생성) 아무것도 렌더하지 않는다.

  import { ADSENSE_CLIENT } from '@/lib/ads'

  interface Props {
    slot: string;
    format?: string;
    layoutKey?: string;
    variant: 'infeed' | 'article';
  }

  let { slot, format = 'auto', layoutKey, variant }: Props = $props()

  let ins = $state<HTMLElement | undefined>(undefined)
  let enabled = $state(false)

  $effect(() => {
    if (!slot) return
    if (typeof document === 'undefined') return
    if (!document.querySelector('meta[name="google-adsense-account"]')) return
    enabled = true
  })

  $effect(() => {
    if (!enabled || !ins) return
    // 같은 ins 에 이중 push 금지 — Inertia 로 재마운트돼도 상태가 남아있을 수 있다.
    if (ins.getAttribute('data-adsbygoogle-status')) return
    try {
      // @ts-expect-error adsbygoogle 는 전역 스크립트가 주입한다
      ;(window.adsbygoogle = window.adsbygoogle || []).push({})
    } catch (e) {
      console.warn('[AdSlot] adsbygoogle push failed', e)
    }
  })
</script>

{#if enabled}
  <div class="ad-slot" role="complementary" aria-label="광고 영역">
    <span class="ad-slot__label">광고</span>
    {#if variant === 'infeed'}
      <ins
        bind:this={ins}
        class="adsbygoogle"
        style="display:block"
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot}
        data-ad-format={format}
        data-ad-layout-key={layoutKey}
      ></ins>
    {:else}
      <ins
        bind:this={ins}
        class="adsbygoogle"
        style="display:block"
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      ></ins>
    {/if}
  </div>
{/if}

<style>
  /* 🔴 기본은 「광고」 크롬(라벨·배경·여백)을 숨기고, 광고가 실제로 채워진
     (`data-ad-status="filled"`) 뒤에만 보인다(2026-09-25 리뷰). 반대로 두면
     승인 전·광고 차단기·응답 전 구간에 빈 「광고」 카드가 목록에 상주한다.
     ins 자체는 숨기지 않는다 — display:none 이면 폭이 0 이라 AdSense 가
     채우지 못한다. data-ad-status 는 스크립트가 런타임에 붙이므로 :global. */
  .ad-slot {
    word-break: keep-all;
    overflow-wrap: break-word;
  }

  .ad-slot__label {
    display: none;
    font-size: 11px;
    color: var(--content-tertiary);
    margin-bottom: 6px;
  }

  .ad-slot:has(:global(ins[data-ad-status='filled'])) {
    margin: 24px 0;
    padding: 12px;
    border-radius: 1rem;
    background-color: var(--surface-tertiary);
    border: 1px solid var(--border);
  }

  .ad-slot:has(:global(ins[data-ad-status='filled'])) .ad-slot__label {
    display: block;
  }

  .ad-slot:has(:global(ins[data-ad-status='unfilled'])) {
    display: none;
  }
</style>
